import { useEffect, useState } from 'react';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import GlobalStyles from '@mui/material/GlobalStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import Carousel from '../../../components/carousels/social-proof/Carousel';
import { CarouselButtons } from '../../../components/CarouselButtons';
import { ITrackingPayload } from '../../../types/analytics';
import { ANALYTICS_CONSTANTS, trackEDDL } from '../../../utilities/analytics';
import { getSocialProofPosts, ISocialProofApiPost } from '../../../api/social-proof-api';
import { SocialProofSlide } from './SocialProofSlide';

import './SocialProof.scss';
import { CarouselSkipLink } from '../../../components/CarouselSkipLink';

interface ISocialProofProps {
    fullStoryPageId: string;
    nextFocus: string;
}

export const SocialProof = ({ fullStoryPageId, nextFocus }: ISocialProofProps) => {
    const theme = useTheme();
    const isMobile = !useMediaQuery(theme.breakpoints.up('sm'));
    const [isLoading, setIsLoading] = useState(false);
    const [socialProofCardsLoaded, setSocialProofCardsLoaded] = useState(false);
    const [socialProofPosts, setSocialProofPosts] = useState<ISocialProofApiPost[]>([]);
    const [carouselHeight, setCarouselHeight] = useState<number | null>(null);

    useEffect(() => {
        setIsLoading(true);
        getSocialProofPosts().then((posts) => {
            setIsLoading(false);
            setSocialProofPosts(posts);
            setSocialProofCardsLoaded(true);
        });
    }, []);

    useEffect(() => {
        let script: HTMLScriptElement | null = null;
        if (!isLoading && socialProofPosts.length) {
            const existingScript = document.querySelector('script[src="/scripts/facebook-embed.js"]');
            if (!existingScript) {
                script = document.createElement('script');
                script.src = '/scripts/facebook-embed.js';
                script.async = true; // Or script.defer = true;
                document.head.appendChild(script);
            }
        }
        // Cleanup function to remove the script if it was added by this effect
        return () => {
            if (script && script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, [isLoading, socialProofPosts]);

    useEffect(() => {
        if (!socialProofCardsLoaded) return;
        const el = document.getElementById('social-proof-carousel-slides-mw');
        if (el) {
            const spacing = isMobile ? 32 : 16;
            setCarouselHeight(el.offsetHeight + spacing);
        }
    }, [socialProofCardsLoaded, isMobile]);

    if (isLoading || !socialProofCardsLoaded) {
        return <></>;
    }

    return (
        <Stack direction="column" spacing={{ xs: 2, md: 4 }} data-fs-section={`${fullStoryPageId}: Social Proof`}>
            {carouselHeight !== null && (
                <GlobalStyles
                    styles={{ '#social-proof-carousel-slides-mw': { height: `${carouselHeight}px !important` } }}
                />
            )}
            <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                <Typography
                    component="h2"
                    variant="h5"
                    sx={{
                        typography: { sm: 'h4' },
                        maxWidth: { md: 660, lg: 900 },
                    }}
                >
                    Driven by happy customers for over 30 years
                </Typography>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    <CarouselButtons sectionIdPrefix="social-proof" ariaText="Social Proof" />
                </Box>
            </Stack>
            <CarouselSkipLink
                id="skip-link__social-proof"
                href="#research-articles-component"
                text="Skip Social Proof Carousel"
            />
            <Carousel
                itemsLoaded={socialProofCardsLoaded}
                itemCount={socialProofPosts.length}
                name="social-proof"
                nextFocus={nextFocus}
                onIndexChanged={() => {
                    const payload: ITrackingPayload = {
                        event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                        linkDetails: {
                            name: 'IG Social Proof Slide View',
                        },
                    };
                    trackEDDL(payload);
                }}
            >
                {socialProofPosts.map((post) => (
                    <SocialProofSlide key={post.postId} post={post} />
                ))}
            </Carousel>
        </Stack>
    );
};
